import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useSitemapPageMutations } from '~/hooks/use-sitemap-pages';
import { sitemapPageSchema, EMPTY_SITEMAP_PAGE, type SitemapPageFormValues } from '~/lib/schemas/sitemap-page';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: SitemapPageFormValues | null;
}

/** Add / edit a sitemap page (code + navigate URL). */
export function SitemapPageEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useSitemapPageMutations();
	const form = useForm<SitemapPageFormValues>({
		resolver: zodResolver(sitemapPageSchema) as Resolver<SitemapPageFormValues>,
		defaultValues: initial ?? EMPTY_SITEMAP_PAGE,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_SITEMAP_PAGE);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.sitemapPages.editTitle') : t('system.sitemapPages.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.sitemapPages.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('system.sitemapPages.columns.code')} error={formState.errors.code?.message}>
						<Input {...register('code')} maxLength={50} aria-invalid={Boolean(formState.errors.code)} autoFocus />
					</FormField>
					<FormField label={t('system.sitemapPages.columns.url')} error={formState.errors.url?.message}>
						<Input {...register('url')} maxLength={150} aria-invalid={Boolean(formState.errors.url)} />
					</FormField>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
